import { client } from "./client";
import { BASE_URL } from "./config";

export const BASE = BASE_URL;

// ✅ FETCH ALL PRODUCTS (pagination via response body)
export async function fetchProducts({ token, page = 1, perPage = 100 }) {
    return client.get(`/products?page=${page}&per_page=${perPage}`, { token });
}

// ✅ FETCH SINGLE PRODUCT
export async function getProductById({ id, token }) {
    if (!id) throw new Error("Product ID missing");
    const json = await client.get(`/product/${id}`, { token });
    return json?.data ?? json;
}

// ✅ SEARCH (returns matching products only) + pagination
export async function searchProducts({
    query,
    q,
    token,
    page = 1,
    perPage = 100,
    per_page,
    categoryId,
    category_id,
    copyId,
    copy_id,
    smartProductId,
    smart_product_id,
}) {
    const searchQuery = q !== undefined ? q : (query || "");
    const finalPage = page;
    const finalPerPage = per_page !== undefined ? per_page : perPage;
    const finalCategoryId = category_id !== undefined ? category_id : categoryId;
    const finalCopyId = copy_id !== undefined ? copy_id : copyId;
    const finalSmartProductId = smart_product_id !== undefined ? smart_product_id : smartProductId;

    let url = `/products/search?q=${encodeURIComponent(searchQuery)}&page=${finalPage}&per_page=${finalPerPage}`;
    if (finalCategoryId) {
        url += `&category_id=${finalCategoryId}`;
    }
    if (finalCopyId) {
        url += `&copy_id=${finalCopyId}`;
    }
    if (finalSmartProductId) {
        url += `&smart_product_id=${finalSmartProductId}`;
    }
    const json = await client.get(url, { token });
    return {
        data: json?.data || [],
        pagination: json?.pagination || null,
    };
}

// ✅ GET PRODUCT CATEGORIES
export async function getProductCategories({ token }) {
    return client.get(`/get_product_categories`, { token });
}

// ✅ FETCH HISTORY FOR A PRODUCT
export async function getProductOrdersTimeline({
    productId,
    token,
    page = 1,
    perPage = 10,
}) {
    return client.get(`/product/orders-timeline?product_id=${productId}&page=${page}&per_page=${perPage}`, { token });
}

// ✅ ADD DAMAGE TO A PRODUCT
export async function addDamage({ token, payload }) {
    return client.post(`/add_damage`, payload, { token });
}

// ✅ GET DAMAGE HISTORY BY ORDER ID
export async function getDamageHistory({ token, addedOrderId }) {
    return client.get(`/product/get_damage?added_order_id=${addedOrderId}`, { token });
}

// ✅ GET MEMBERS LIST
export async function getMembers({ token }) {
    return client.get(`/get_members`, { token });
}

// ✅ GET MEMBER DETAILS
export const getMemberById = async ({ token, userId }) => {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_member?user_id=${userId}`, { token });
};

// ✅ GET BOOKSHELF BY MEMBER (user_id)
export async function getBookShelfByUser({ userId, token }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/getbookshelf?user_id=${userId}`, { token });
}

// ✅ ADD NEW MEMBER
export async function addMember({ token, payload }) {
    return client.post(`/add_member`, payload, { token });
}

// ✅ GET STORES LIST
export async function getStores({ token }) {
    return client.get(`/get_stores`, { token });
}

// ✅ GET USER SUBSCRIPTIONS BY MEMBER ID
export async function getUserSubscriptions({ token, userId }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_user_subscriptions?user_id=${userId}`, { token });
}

// ✅ GET SUBSCRIPTION PRODUCTS (Plans list)
export async function getSubscriptionProducts({ token }) {
    return client.get(`/get_subscription_products`, { token });
}

// ✅ GET SUBSCRIPTION BY ID
export const getSubscriptionById = async ({ token, subscriptionId }) => {
    return client.get(`/get_subscription_by_id?subscription_id=${subscriptionId}`, { token });
};

// ✅ GET USER CART BY USER ID
export const getUserCart = async ({ token, userId }) => {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_user_cart?user_id=${userId}`, { token });
};

// ✅ GET ORDER IDS BY USER
export const getOrderIdsByUser = async ({ token, userId }) => {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_order_ids?user_id=${userId}`, { token });
};

// ✅ GET DELIVERY SLOTS
export async function getDeliverySlots(args) {
    let token = null;
    let pincode = "";
    if (typeof args === "object" && args !== null) {
        token = args.token || localStorage.getItem("token") || sessionStorage.getItem("token");
        pincode = args.pincode || args.pinCode || "";
    } else {
        pincode = args;
        token = localStorage.getItem("token") || sessionStorage.getItem("token");
    }
    return client.get(`/delivery_slots?pincode=${pincode}`, { token });
}

// ✅ UPDATE BOOK HOLD STATUS
export async function updateBookHoldStatus({ token, payload }) {
    return client.post(`/update_book_hold_status`, payload, { token });
}

// ✅ GET ORDERS BY USER
export async function getOrdersByUser({ token, userId }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_orders_by_user?user_id=${userId}`, { token });
}

// ✅ GET READING HISTORY
export async function getReadingHistory({ token, userId }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_reading_history?user_id=${userId}`, { token });
}

// ✅ GET USER ANALYTICS
export async function getUserAnalytics({ token, userId }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/user_analytics?user_id=${userId}`, { token });
}

// ✅ GET WISHLIST
export async function getWishlist({ token, userId }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_wishlist?user_id=${userId}`, { token });
}

// ✅ GET REWARD POINTS
export async function getRewardPoints({ token, userId }) {
    if (!userId) throw new Error("User ID missing");
    return client.get(`/get_reward_points?user_id=${userId}`, { token });
}

// ✅ GET ALL ORDERS LIST (with search, status, date_from, date_to and pagination)
export async function getAllOrdersList({
    token,
    page = 1,
    perPage = 20,
    search = "",
    status = "",
    date_from = "",
    date_to = ""
}) {
    let url = `/get_all_orders_list?page=${page}&per_page=${perPage}`;
    if (search) {
        url += `&search=${encodeURIComponent(String(search))}`;
    }
    if (status && status !== "all") {
        url += `&status=${encodeURIComponent(String(status))}`;
    }
    if (date_from) {
        url += `&date_from=${encodeURIComponent(String(date_from))}`;
    }
    if (date_to) {
        url += `&date_to=${encodeURIComponent(String(date_to))}`;
    }
    return client.get(url, { token });
}

// ✅ CANCEL ORDER
export async function cancelOrder({ token, orderId }) {
    return client.post(`/cancel_order`, { order_id: String(orderId) }, { token });
}

// ✅ GET ORDER DETAILS
export async function getOrderDetails({ token, orderId }) {
    if (!orderId) throw new Error("Order ID missing");
    return client.get(`/get_order_details?order_id=${orderId}`, { token });
}

// ✅ APPLY COUPON
export async function applyCoupon({ token, couponCode }) {
    return client.post(`/apply_coupon`, { coupon_code: couponCode }, { token });
}

// ✅ APPLY GIFT CARD
export async function applyGiftCard({ token, giftCardCode }) {
    return client.post(`/apply_gift_card`, { gift_card: giftCardCode }, { token });
}

// ✅ SEARCH MEMBERS
export async function searchMembers({ token, query }) {
    return client.get(`/search_members?q=${encodeURIComponent(query)}`, { token });
}

// ✅ CHECKOUT ORDER
export async function checkoutOrder({ token, payload }) {
    return client.post(`/checkout`, payload, { token });
}

// ✅ REQUEST REFUND
export async function requestRefund({ token, payload }) {
    return client.post(`/request_refund`, payload, { token });
}

let orderStatusesCache = null;
let orderStatusesPromise = null;

// ✅ GET ORDER STATUSES
export async function getOrderStatuses({ token } = {}) {
    if (orderStatusesCache) {
        return orderStatusesCache;
    }
    if (orderStatusesPromise) {
        return orderStatusesPromise;
    }
    orderStatusesPromise = client.get(`/order_statuses`, { token })
        .then((res) => {
            orderStatusesCache = res;
            orderStatusesPromise = null;
            return res;
        })
        .catch((err) => {
            orderStatusesPromise = null;
            throw err;
        });
    return orderStatusesPromise;
}

// ✅ UPDATE ORDER
export async function updateOrder({ token, payload }) {
    return client.post(`/update_order`, payload, { token });
}


